import { Stat } from '@salonomy/ui';
import { useOccupancyReport, type ReportRange } from '../../data/reports.js';
import { exportCsvFilename } from '../../lib/csv.js';
import { percentFromBps } from '../../lib/format.js';
import { ReportCard, type CsvExport } from './shared.js';

/**
 * WS9 — chair occupancy: booked vs available chair-minutes by staff, with the salon-wide utilisation headline.
 * Utilisation arrives as basis points (server-computed) → percentFromBps; minutes are integer counts, not
 * money, so they pass through verbatim. No JS recomputes the utilisation ratio (ADR-0009 discipline).
 */

/** Whole minutes → "Hh Mm" / "Mm" for a compact at-a-glance read (display only — no money). */
function asHours(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

export function OccupancyCard({ range }: { range: ReportRange }) {
  const { data: report, isLoading, isError } = useOccupancyReport(range);

  const isEmpty = !!report && report.byStaff.length === 0;

  const csv: CsvExport | null =
    report && report.byStaff.length > 0
      ? {
          filename: exportCsvFilename('occupancy'),
          headers: ['Staff', 'Available minutes', 'Booked minutes', 'Utilisation'],
          rows: report.byStaff.map((s) => [
            s.staffName,
            s.availableMinutes,
            s.bookedMinutes,
            percentFromBps(s.utilisationBps),
          ]),
        }
      : null;

  return (
    <ReportCard
      title="Occupancy"
      subtitle="Booked vs available chair-minutes over this period, by stylist. Utilisation is server-computed."
      isLoading={isLoading}
      isError={isError}
      isEmpty={isEmpty}
      emptyLabel="No chair availability in this period."
      csv={csv}
    >
      {report ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
            <Stat label="Utilisation" value={percentFromBps(report.utilisationBps)} />
            <Stat label="Booked" value={asHours(report.bookedChairMinutes)} />
            <Stat label="Available" value={asHours(report.availableChairMinutes)} />
          </div>

          <div className="settings-list">
            {report.byStaff.map((s) => (
              <div key={s.staffId} className="settings-row">
                <div>
                  <div style={{ fontWeight: 600 }}>{s.staffName}</div>
                  <div className="muted" style={{ fontSize: 12.5 }}>
                    {asHours(s.bookedMinutes)} booked of {asHours(s.availableMinutes)} available
                  </div>
                </div>
                <Stat label="Utilisation" value={percentFromBps(s.utilisationBps)} />
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </ReportCard>
  );
}
